import { AfterViewInit, Component, ElementRef, EventEmitter, Input, NgZone, OnChanges, OnDestroy, Output, SimpleChanges, ViewChild } from '@angular/core';
import { Subject, takeUntil } from 'rxjs';
import { CommonModule } from '@angular/common';
import { AbstractControl, FormBuilder, FormGroup, ReactiveFormsModule, ValidationErrors, Validators } from '@angular/forms';
import { TranslateModule } from '@ngx-translate/core';
import { ToasterService } from '../../../../../../services/toaster.service';
import { AiCounselorService } from '../../../../../../services/ai-counselor.service';

/** Recording longer than this auto-stops and transcribes what was captured so far. */
const MAX_RECORDING_MS = 10_000;

@Component({
  selector: 'mindbase-chat-input',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, TranslateModule],
  templateUrl: './chat-input.component.html',
  styleUrl: './chat-input.component.scss'
})
export class ChatInputComponent implements AfterViewInit, OnChanges, OnDestroy {
  private readonly destroy$ = new Subject<void>();
  private mediaRecorder: MediaRecorder | null = null;
  private mediaStream: MediaStream | null = null;
  private recordedChunks: Blob[] = [];
  private autoStopTimer: ReturnType<typeof setTimeout> | null = null;
  private discardNextRecording = false;
  private _isSending = false;

  @ViewChild('textareaRef') private readonly textareaRef!: ElementRef<HTMLTextAreaElement>;

  @Input() placeholder = 'Answer a few questions or ask me anything.';
  @Input() set isSending(val: boolean) {
    const wasSending = this._isSending;
    this._isSending = val;
    if (wasSending && !val) {
      setTimeout(() => this.textareaRef?.nativeElement.focus(), 0);
    }
  }
  get isSending(): boolean { return this._isSending; }
  /** True while the chatbot is unavailable (GET /user/chatbot disable window) — disables sending/recording. */
  @Input() disabled = false;
  /** "You will be able to use the chatbot after <date>." — shown as the placeholder while `disabled`, in place of the generic unavailable text. */
  @Input() blockedMessage = '';
  /** i18n key for the generic unavailable placeholder shown while `disabled` and `blockedMessage` is empty — varies by role (student vs. tutor/counsellor). */
  @Input() unavailableMessageKey = 'CopilotUnavailableMessage';
  @Output() messageSent = new EventEmitter<string>();

  isRecording = false;
  isTranscribing = false;

  /** True when the browser can record microphone audio (MediaRecorder + getUserMedia). */
  readonly isSpeechSupported: boolean =
    typeof navigator !== 'undefined' &&
    !!navigator.mediaDevices?.getUserMedia &&
    typeof MediaRecorder !== 'undefined';

  readonly form: FormGroup;

  ngAfterViewInit(): void {
    this.textareaRef?.nativeElement.focus();
  }

  /**
   * Reactive Forms owns the native `disabled` attribute for a `formControlName` element —
   * an `[attr.disabled]` binding on the same element gets fought over (and typically loses)
   * once the control's own value/validity updates, which is exactly what happens on every
   * keystroke. So the block state has to go through the control's disable()/enable() instead.
   */
  ngOnChanges(changes: SimpleChanges): void {
    if (!changes['disabled']) return;
    const control = this.form.get('message');
    if (this.disabled) {
      control?.disable();
    } else {
      control?.enable();
    }
  }

  constructor(
    private readonly fb: FormBuilder,
    private readonly ngZone: NgZone,
    private readonly toasterService: ToasterService,
    private readonly aiCounselorService: AiCounselorService
  ) {
    this.form = this.fb.group({
      message: ['', [Validators.required, Validators.minLength(1), this.maxWordsValidator()]]
    });
  }

  readonly maxWords = 5000;

  get wordCount(): number {
    const val: string = this.form.get('message')?.value ?? '';
    return val.trim() ? val.trim().split(/\s+/).length : 0;
  }

  get isOverLimit(): boolean {
    return this.wordCount > this.maxWords;
  }

  private maxWordsValidator() {
    return (control: AbstractControl): ValidationErrors | null => {
      const val: string = control.value ?? '';
      const count = val.trim() ? val.trim().split(/\s+/).length : 0;
      return count > this.maxWords ? { maxWords: { actual: count, max: this.maxWords } } : null;
    };
  }

  send(): void {
    if (this.isSending || this.disabled) return;
    const message = this.form.get('message')?.value?.trim() as string;
    if (!message) return;
    this.stopRecording();
    this.messageSent.emit(message);
    this.form.reset({ message: '' });
  }

  onKeyDown(event: KeyboardEvent): void {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      if (!this.isSending && !this.disabled) this.send();
    }
  }

  toggleSpeech(): void {
    if (this.disabled) return;
    if (!this.isSpeechSupported) {
      this.toasterService.infoToastr('Voice input requires microphone access in this browser.');
      return;
    }
    if (this.isRecording) {
      this.applyRecording();
    } else {
      void this.startRecording();
    }
  }

  /** Confirms the in-progress recording: stops it and transcribes what was captured. */
  applyRecording(): void {
    if (!this.isRecording) return;
    this.discardNextRecording = false;
    this.stopRecording();
  }

  /** Discards the in-progress recording without transcribing or touching the message textarea. */
  cancelRecording(): void {
    if (!this.isRecording) return;
    this.discardNextRecording = true;
    this.stopRecording();
  }

  private async startRecording(): Promise<void> {
    if (this.isRecording || this.isTranscribing) return;
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      this.ngZone.run(() => {
        this.mediaStream = stream;
        this.recordedChunks = [];
        const recorder = new MediaRecorder(stream);
        recorder.ondataavailable = (e: BlobEvent) => {
          if (e.data.size > 0) this.recordedChunks.push(e.data);
        };
        recorder.onstop = () => this.ngZone.run(() => {
          if (this.discardNextRecording) {
            this.discardNextRecording = false;
            this.recordedChunks = [];
            this.mediaRecorder = null;
            return;
          }
          this.transcribeRecording();
        });
        this.mediaRecorder = recorder;
        recorder.start();
        this.isRecording = true;
        this.autoStopTimer = setTimeout(() => this.ngZone.run(() => this.stopRecording()), MAX_RECORDING_MS);
      });
    } catch {
      this.toasterService.errToastr('Microphone access was denied.');
    }
  }

  private stopRecording(): void {
    if (this.autoStopTimer !== null) {
      clearTimeout(this.autoStopTimer);
      this.autoStopTimer = null;
    }
    if (this.mediaRecorder && this.mediaRecorder.state !== 'inactive') {
      this.mediaRecorder.stop();
    }
    this.mediaStream?.getTracks().forEach(track => track.stop());
    this.mediaStream = null;
    this.isRecording = false;
  }

  private transcribeRecording(): void {
    const chunks = this.recordedChunks;
    this.recordedChunks = [];
    this.mediaRecorder = null;
    if (!chunks.length) return;

    const blob = new Blob(chunks, { type: chunks[0].type || 'audio/webm' });
    this.isTranscribing = true;
    this.aiCounselorService
      .speechToText(blob)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (res) => {
          this.isTranscribing = false;
          const text = res.data?.text?.trim();
          if (!text) return;
          const current: string = this.form.get('message')?.value ?? '';
          const merged = current.trim() ? `${current.trim()} ${text}` : text;
          this.form.patchValue({ message: merged });
        },
        error: () => {
          this.isTranscribing = false;
          this.toasterService.errToastr('Could not transcribe audio. Please try again.');
        },
      });
  }

  ngOnDestroy(): void {
    this.stopRecording();
    this.destroy$.next();
    this.destroy$.complete();
  }
}
